import { useEffect, useMemo, useState } from 'react';
import { EyeOff, FlaskConical, RefreshCw, Save } from 'lucide-react';
import type {
  DryRunUpstreamErrorPolicyRuleResponse,
  UpstreamErrorActionSeen,
  UpstreamErrorObservation,
  UpstreamErrorPolicyAction,
  UpstreamErrorPolicyKind,
  UpstreamErrorPolicyMatchMode,
  UpstreamErrorPolicyMessageMode,
  UpstreamErrorPolicyPublicCode,
  UpstreamErrorPolicyRule,
  UpstreamErrorReviewStatus,
} from '@nadoutong/contracts';
import {
  useCreateUpstreamErrorPolicyRule,
  useDryRunUpstreamErrorPolicyRule,
  useIgnoreUpstreamErrorObservation,
  useUpdateUpstreamErrorPolicyRule,
  useUpstreamErrorObservations,
  useUpstreamErrorPolicyRules,
} from '../api/useUpstreamErrorPolicy.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import {
  Button,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

interface RuleDraft {
  readonly name: string;
  readonly priority: string;
  readonly enabled: boolean;
  readonly statusMin: string;
  readonly statusMax: string;
  readonly rawType: string;
  readonly rawCode: string;
  readonly rawTypeMatch: UpstreamErrorPolicyMatchMode;
  readonly rawCodeMatch: UpstreamErrorPolicyMatchMode;
  readonly requiredTerms: string;
  readonly excludedTerms: string;
  readonly action: UpstreamErrorPolicyAction;
  readonly publicCode: UpstreamErrorPolicyPublicCode;
  readonly httpStatus: string;
  readonly errorType: string;
  readonly messageMode: UpstreamErrorPolicyMessageMode;
  readonly fixedMessage: string;
  readonly kind: UpstreamErrorPolicyKind;
}

/**
 * Rules cover the whole tenant-facing vocabulary now, not just the two
 * validation codes (#1437). Listing them here rather than deriving from a
 * contract constant keeps the dropdown order deliberate: the codes an operator
 * actually reaches for when mapping an upstream error come first.
 */
const PUBLIC_CODE_OPTIONS: readonly UpstreamErrorPolicyPublicCode[] = [
  'REQUEST_NOT_SUPPORTED',
  'REQUEST_IMAGE_TOO_LARGE',
  'MODEL_BUSY',
  'MODEL_TIMEOUT',
  'MODEL_SERVICE_UNAVAILABLE',
  'MODEL_NOT_AVAILABLE',
  'INVALID_REQUEST',
  'AUTHENTICATION_FAILED',
  'ACCOUNT_UNAVAILABLE',
  'SERVICE_SUSPENDED',
  'INSUFFICIENT_BALANCE',
  'PLATFORM_SERVICE_UNAVAILABLE',
  'PLATFORM_INTERNAL_ERROR',
  // `UPSTREAM_PASSTHROUGH` is deliberately absent. It is what the resolver
  // stamps on an error nothing mapped, not something to map *to*: a `map` rule
  // naming it would render `api_error` under an operator-chosen status, a
  // hybrid with no meaning. To get a passthrough, disable the mapping rule or
  // set the rule's action to `passthrough`.
];

/**
 * Everything the wire contract permits, which is a superset of what the
 * dropdown offers. Editing a rule must not silently rewrite a code this UI
 * chooses not to suggest.
 */
const KNOWN_PUBLIC_CODES: readonly UpstreamErrorPolicyPublicCode[] = [
  ...PUBLIC_CODE_OPTIONS,
  'UPSTREAM_PASSTHROUGH',
  // Emitted by the gateway's unit-metering fail-closed gate (#1653), not
  // suggested as a rule target — but a rule that already carries it must not
  // be silently rewritten to the fallback code on edit.
  'MODEL_BILLING_UNAVAILABLE',
];

const KIND_OPTIONS: readonly UpstreamErrorPolicyKind[] = [
  'V01', 'V02', 'B01', 'R01', 'T01', 'M01', 'M10', 'Q01', 'Q10',
  'A01', 'A10', 'F01', 'I01', 'I02', 'P01', 'S01', 'U01',
  // Support-code kind carried by an unmapped, passed-through upstream error.
  'X01',
];

const ACTION_SEEN_LABEL: Record<UpstreamErrorActionSeen, string> = {
  passthrough: 'passed through',
  mapped: 'mapped',
  procurement_fault: 'procurement fault',
};

const REVIEW_STATUS_OPTIONS: ReadonlyArray<UpstreamErrorReviewStatus | 'all'> = [
  'unreviewed',
  'rule_attached',
  'ignored',
  'all',
];

function toReviewStatus(value: string): UpstreamErrorReviewStatus | 'all' {
  return REVIEW_STATUS_OPTIONS.find((status) => status === value) ?? 'all';
}

const EMPTY_DRY_RUN: DryRunUpstreamErrorPolicyRuleResponse = {
  match: false,
  action: null,
  publicCode: null,
  message: null,
  kind: null,
  httpStatus: null,
  errorType: null,
};

export function UpstreamErrorPolicyPage(): JSX.Element {
  const toast = useToast();
  const [reviewStatus, setReviewStatus] = useState<UpstreamErrorReviewStatus | 'all'>('unreviewed');
  const observations = useUpstreamErrorObservations(reviewStatus);
  const rules = useUpstreamErrorPolicyRules();
  const dryRun = useDryRunUpstreamErrorPolicyRule();
  const createRule = useCreateUpstreamErrorPolicyRule();
  const updateRule = useUpdateUpstreamErrorPolicyRule();
  const ignoreObservation = useIgnoreUpstreamErrorObservation();

  const [selectedFingerprint, setSelectedFingerprint] = useState<string | null>(null);
  const selectedObservation = useMemo(() => {
    const rows = observations.data ?? [];
    if (rows.length === 0) return null;
    return rows.find((row) => row.fingerprint === selectedFingerprint) ?? rows[0] ?? null;
  }, [observations.data, selectedFingerprint]);
  const [draft, setDraft] = useState<RuleDraft>(() => draftFromObservation(null));

  useEffect(() => {
    if (selectedObservation === null) {
      setSelectedFingerprint(null);
      setDraft(draftFromObservation(null));
      return;
    }
    setSelectedFingerprint((current) => current ?? selectedObservation.fingerprint);
    setDraft(draftFromObservation(selectedObservation));
    dryRun.reset();
  }, [selectedObservation?.fingerprint]);

  const activeRules = (rules.data ?? []).filter((rule) => rule.enabled).length;
  const unreviewedCount = (observations.data ?? []).filter((row) => row.reviewStatus === 'unreviewed').length;

  function patchDraft<K extends keyof RuleDraft>(key: K, value: RuleDraft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function handleDryRun(): void {
    if (selectedObservation === null) return;
    dryRun.mutate({
      observationFingerprint: selectedObservation.fingerprint,
      rule: draftToInput(draft),
    }, {
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  function handleCreateRule(): void {
    if (selectedObservation === null) return;
    createRule.mutate({
      ...draftToInput(draft),
      attachObservationFingerprint: selectedObservation.fingerprint,
    }, {
      onSuccess: () => toast.success('Policy rule saved'),
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  function handleIgnore(): void {
    if (selectedObservation === null) return;
    ignoreObservation.mutate(selectedObservation.fingerprint, {
      onSuccess: () => toast.success('Observation ignored'),
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  function handleDisableRule(rule: UpstreamErrorPolicyRule): void {
    updateRule.mutate({
      id: rule.id,
      body: { enabled: false },
    }, {
      onSuccess: () => toast.success('Policy rule disabled'),
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  return (
    <section className="flex flex-col gap-6" data-testid="upstream-error-policy-page">
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip variant="neutral" label="Gateway SQL" />
        <StatusChip variant={unreviewedCount > 0 ? 'warning' : 'ok'} label={`${unreviewedCount} unreviewed`} />
        <StatusChip variant="ok" label={`${activeRules} active rules`} />
      </div>
      <PageHeader
        title="Error Mapping Policy"
        sub="Upstream errors enter review before they affect tenant-facing envelopes. Rule changes take effect within about 30 seconds across the fleet."
        actions={
          <>
            <Button
              type="button"
              onClick={() => {
                void observations.refetch();
                void rules.refetch();
              }}
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleCreateRule}
              disabled={selectedObservation === null || createRule.isPending}
              data-testid="upstream-error-policy-save-rule"
            >
              <Save aria-hidden="true" className="h-4 w-4" />
              Save Rule
            </Button>
          </>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.55fr)]">
        <ObservationTable
          observations={observations.data ?? []}
          loading={observations.isLoading}
          reviewStatus={reviewStatus}
          onReviewStatusChange={setReviewStatus}
          selectedFingerprint={selectedObservation?.fingerprint ?? null}
          onSelect={setSelectedFingerprint}
        />
        <ReviewConsole
          observation={selectedObservation}
          draft={draft}
          dryRunResult={dryRun.data ?? EMPTY_DRY_RUN}
          dryRunPending={dryRun.isPending}
          createPending={createRule.isPending}
          ignorePending={ignoreObservation.isPending}
          onPatch={patchDraft}
          onDryRun={handleDryRun}
          onSave={handleCreateRule}
          onIgnore={handleIgnore}
        />
      </div>

      <RulesTable
        rules={rules.data ?? []}
        loading={rules.isLoading}
        disablingRuleId={updateRule.variables?.id ?? null}
        disablePending={updateRule.isPending}
        onDisable={handleDisableRule}
      />
    </section>
  );
}

function ObservationTable({
  observations,
  loading,
  reviewStatus,
  onReviewStatusChange,
  selectedFingerprint,
  onSelect,
}: {
  readonly observations: readonly UpstreamErrorObservation[];
  readonly loading: boolean;
  readonly reviewStatus: UpstreamErrorReviewStatus | 'all';
  readonly onReviewStatusChange: (value: UpstreamErrorReviewStatus | 'all') => void;
  readonly selectedFingerprint: string | null;
  readonly onSelect: (fingerprint: string) => void;
}): JSX.Element {
  return (
    <Panel
      caption="Observations"
      padded
      className="min-h-[540px]"
      actions={
        <Select
          value={reviewStatus}
          onValueChange={(value) => onReviewStatusChange(toReviewStatus(value))}
          ariaLabel="Review status"
          mono
          className="h-8 w-[170px]"
          options={REVIEW_STATUS_OPTIONS.map((status) => ({ value: status, label: status }))}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">
          Sanitized upstream validation messages grouped by fingerprint.
        </p>
        {loading ? (
          <TableSkeleton rows={6} rowHeight={52} testId="upstream-error-policy-observations-loading" />
        ) : observations.length === 0 ? (
          <div className="rounded-lg bg-well">
            <EmptyState title="No observations" />
          </div>
        ) : (
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>Message</Th>
                <Th>Context</Th>
                <Th num>Count</Th>
                <Th>Last seen</Th>
              </Tr>
            </THead>
            <TBody>
              {observations.map((row) => (
                <Tr
                  key={row.fingerprint}
                  data-testid={`upstream-error-observation-${row.fingerprint}`}
                  onClick={() => onSelect(row.fingerprint)}
                  className={
                    row.fingerprint === selectedFingerprint
                      ? 'cursor-pointer bg-active'
                      : 'cursor-pointer'
                  }
                >
                  <Td className="max-w-[520px] align-top">
                    <div className="font-mono text-micro leading-5 text-paper">
                      {row.sanitizedMessage}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <StatusChip variant={reviewVariant(row.reviewStatus)} label={row.reviewStatus} />
                      {row.actionSeen !== null ? (
                        <StatusChip
                          variant={actionSeenVariant(row.actionSeen)}
                          label={ACTION_SEEN_LABEL[row.actionSeen]}
                        />
                      ) : null}
                      <span className="font-mono text-micro text-paper-low">{row.fingerprint}</span>
                    </div>
                  </Td>
                  <Td className="align-top">
                    <div>{row.vendor ?? 'unknown'} / {row.protocol ?? 'unknown'}</div>
                    <div className="mt-1 font-mono text-micro text-paper-low">{row.model ?? 'unknown'}</div>
                    <div className="mt-1 font-mono text-micro text-paper-low">HTTP {row.rawStatus}</div>
                  </Td>
                  <Td num emphasis className="align-top">
                    {row.count}
                  </Td>
                  <Td mono className="align-top">
                    {formatLocalTimestamp(row.lastSeenAt, { seconds: true })}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </Panel>
  );
}

function ReviewConsole({
  observation,
  draft,
  dryRunResult,
  dryRunPending,
  createPending,
  ignorePending,
  onPatch,
  onDryRun,
  onSave,
  onIgnore,
}: {
  readonly observation: UpstreamErrorObservation | null;
  readonly draft: RuleDraft;
  readonly dryRunResult: DryRunUpstreamErrorPolicyRuleResponse;
  readonly dryRunPending: boolean;
  readonly createPending: boolean;
  readonly ignorePending: boolean;
  readonly onPatch: <K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) => void;
  readonly onDryRun: () => void;
  readonly onSave: () => void;
  readonly onIgnore: () => void;
}): JSX.Element {
  return (
    <Panel
      caption="Review Console"
      padded
      className="min-h-[540px]"
      testId="upstream-error-policy-review"
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 font-mono text-label text-paper-low">
          {observation?.fingerprint ?? 'No observation selected'}
        </p>
        {observation === null ? (
          <div className="rounded-lg bg-well">
            <EmptyState title="Select an observation" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-well p-4">
              <Meta label="Status" value={`HTTP ${observation.rawStatus}`} />
              <Meta label="Kind" value={observation.rawErrorType ?? 'unknown'} />
              <Meta label="Model" value={observation.model ?? 'unknown'} />
              <Meta label="Fallback" value={observation.publicCodeSeen} />
            </div>

            <div className="flex flex-col gap-4">
              <FormField label="Rule name" fieldId="uep-rule-name">
                <Input
                  id="uep-rule-name"
                  value={draft.name}
                  onChange={(e) => onPatch('name', e.currentTarget.value)}
                />
              </FormField>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Priority" fieldId="uep-priority">
                  <Input
                    id="uep-priority"
                    mono
                    value={draft.priority}
                    onChange={(e) => onPatch('priority', e.currentTarget.value)}
                    inputMode="numeric"
                  />
                </FormField>
                <FormField label="Status min" fieldId="uep-status-min">
                  <Input
                    id="uep-status-min"
                    mono
                    value={draft.statusMin}
                    onChange={(e) => onPatch('statusMin', e.currentTarget.value)}
                    inputMode="numeric"
                  />
                </FormField>
                <FormField label="Status max" fieldId="uep-status-max">
                  <Input
                    id="uep-status-max"
                    mono
                    value={draft.statusMax}
                    onChange={(e) => onPatch('statusMax', e.currentTarget.value)}
                    inputMode="numeric"
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Raw type match" fieldId="uep-raw-type-match">
                  <Select
                    value={draft.rawTypeMatch}
                    onValueChange={(value) =>
                      onPatch('rawTypeMatch', value === 'contains' ? 'contains' : 'exact')
                    }
                    ariaLabel="Raw type match"
                    mono
                    options={[
                      { value: 'exact', label: 'exact' },
                      { value: 'contains', label: 'contains' },
                    ]}
                  />
                </FormField>
                <FormField label="Raw code match" fieldId="uep-raw-code-match">
                  <Select
                    value={draft.rawCodeMatch}
                    onValueChange={(value) =>
                      onPatch('rawCodeMatch', value === 'contains' ? 'contains' : 'exact')
                    }
                    ariaLabel="Raw code match"
                    mono
                    options={[
                      { value: 'exact', label: 'exact' },
                      { value: 'contains', label: 'contains' },
                    ]}
                  />
                </FormField>
              </div>
              <FormField label="Required terms" fieldId="uep-required-terms">
                <Input
                  id="uep-required-terms"
                  mono
                  value={draft.requiredTerms}
                  onChange={(e) => onPatch('requiredTerms', e.currentTarget.value)}
                />
              </FormField>
              <FormField label="Excluded terms" fieldId="uep-excluded-terms">
                <Input
                  id="uep-excluded-terms"
                  mono
                  value={draft.excludedTerms}
                  onChange={(e) => onPatch('excludedTerms', e.currentTarget.value)}
                />
              </FormField>
              <FormField label="Action" fieldId="uep-action">
                <Select
                  value={draft.action}
                  onValueChange={(value) =>
                    onPatch('action', value === 'passthrough' ? 'passthrough' : 'map')
                  }
                  ariaLabel="Action"
                  mono
                  options={[
                    { value: 'map', label: 'map — rewrite to a platform code' },
                    {
                      value: 'passthrough',
                      label: 'passthrough — send the upstream status and sanitized text to the tenant',
                    },
                  ]}
                />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Public code" fieldId="uep-public-code">
                  <Select
                    value={draft.publicCode}
                    onValueChange={(value) => onPatch('publicCode', toPublicCode(value))}
                    ariaLabel="Public code"
                    mono
                    options={publicCodeOptionsFor(draft.publicCode)}
                  />
                </FormField>
                <FormField label="Kind" fieldId="uep-kind">
                  <Select
                    value={draft.kind}
                    onValueChange={(value) => onPatch('kind', toKind(value))}
                    ariaLabel="Kind"
                    mono
                    options={KIND_OPTIONS.map((kind) => ({ value: kind, label: kind }))}
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Downstream status" fieldId="uep-http-status">
                  <Input
                    id="uep-http-status"
                    mono
                    placeholder="keep upstream"
                    value={draft.httpStatus}
                    onChange={(e) => onPatch('httpStatus', e.currentTarget.value)}
                    inputMode="numeric"
                  />
                </FormField>
                <FormField label="Downstream error.type" fieldId="uep-error-type">
                  <Input
                    id="uep-error-type"
                    mono
                    placeholder="derive from public code"
                    value={draft.errorType}
                    onChange={(e) => onPatch('errorType', e.currentTarget.value)}
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-well p-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onPatch('messageMode', 'fixed')}
                  className={draft.messageMode === 'fixed' ? 'bg-active text-paper' : undefined}
                >
                  Fixed
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onPatch('messageMode', 'sanitized_raw')}
                  className={draft.messageMode === 'sanitized_raw' ? 'bg-active text-paper' : undefined}
                >
                  Sanitized raw
                </Button>
              </div>
              <FormField label="Fixed message" fieldId="uep-fixed-message">
                <Textarea
                  id="uep-fixed-message"
                  value={draft.fixedMessage}
                  onChange={(e) => onPatch('fixedMessage', e.currentTarget.value)}
                  className="min-h-[80px] resize-y"
                />
              </FormField>
            </div>

            <DryRunStrip result={dryRunResult} pending={dryRunPending} />

            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                onClick={onDryRun}
                disabled={dryRunPending}
                data-testid="upstream-error-policy-dry-run"
              >
                <FlaskConical aria-hidden="true" className="h-4 w-4" />
                Dry run
              </Button>
              <Button type="button" variant="primary" onClick={onSave} disabled={createPending}>
                <Save aria-hidden="true" className="h-4 w-4" />
                Attach
              </Button>
              <Button type="button" onClick={onIgnore} disabled={ignorePending}>
                <EyeOff aria-hidden="true" className="h-4 w-4" />
                Ignore
              </Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

function RulesTable({
  rules,
  loading,
  disablingRuleId,
  disablePending,
  onDisable,
}: {
  readonly rules: readonly UpstreamErrorPolicyRule[];
  readonly loading: boolean;
  readonly disablingRuleId: string | null;
  readonly disablePending: boolean;
  readonly onDisable: (rule: UpstreamErrorPolicyRule) => void;
}): JSX.Element {
  return (
    <Panel caption="Policy Rules" count={rules.length} padded testId="upstream-error-policy-rules">
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">
          Reviewed rules are evaluated by priority after built-in policy rules.
        </p>
        {loading ? (
          <TableSkeleton rows={4} rowHeight={48} />
        ) : rules.length === 0 ? (
          <div className="rounded-lg bg-well">
            <EmptyState title="No rules configured" />
          </div>
        ) : (
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>Rule</Th>
                <Th>Match</Th>
                <Th>Public</Th>
                <Th>Updated</Th>
                <Th className="text-right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {rules.map((rule) => (
                <Tr key={rule.id}>
                  <Td className="align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-paper">{rule.name}</span>
                      <StatusChip variant={rule.enabled ? 'ok' : 'neutral'} label={rule.enabled ? 'enabled' : 'disabled'} />
                    </div>
                    <div className="mt-1 font-mono text-micro text-paper-low">#{rule.priority} · {rule.id}</div>
                    <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2 text-micro text-paper-low">
                      <span className="font-mono">{rule.sourceKind}</span>
                      {rule.sourceUrl !== null ? (
                        <a
                          href={rule.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-signal underline-offset-2 hover:underline"
                        >
                          source
                        </a>
                      ) : null}
                    </div>
                    {rule.sourceNote !== null ? (
                      <div className="mt-1 max-w-[340px] truncate text-micro text-paper-low">{rule.sourceNote}</div>
                    ) : null}
                  </Td>
                  <Td className="align-top">
                    <div className="font-mono text-micro">HTTP {rule.statusMin ?? '*'}-{rule.statusMax ?? '*'}</div>
                    <div className="mt-1 max-w-[360px] truncate font-mono text-micro text-paper-low">
                      + {rule.requiredTerms.join(', ') || '*'} / - {rule.excludedTerms.join(', ') || '*'}
                    </div>
                    {rule.rawType !== null || rule.rawCode !== null ? (
                      <div className="mt-1 max-w-[360px] truncate font-mono text-micro text-paper-low">
                        {rule.rawType !== null ? `type ${rule.rawTypeMatch} ${rule.rawType}` : null}
                        {rule.rawType !== null && rule.rawCode !== null ? ' · ' : null}
                        {rule.rawCode !== null ? `code ${rule.rawCodeMatch} ${rule.rawCode}` : null}
                      </div>
                    ) : null}
                  </Td>
                  <Td className="align-top">
                    <StatusChip
                      variant={rule.action === 'passthrough' ? 'neutral' : 'ok'}
                      label={rule.action}
                    />
                    {rule.action === 'map' ? (
                      <>
                        <div className="mt-1 font-mono text-micro text-paper">{rule.publicCode}</div>
                        <div className="mt-1 font-mono text-micro text-paper-low">
                          HTTP {rule.httpStatus ?? 'upstream'} · {rule.errorType ?? 'default'}
                        </div>
                        <div className="mt-1 font-mono text-micro text-paper-low">{rule.kind} · {rule.messageMode}</div>
                      </>
                    ) : (
                      <div className="mt-1 font-mono text-micro text-paper-low">falls through to default</div>
                    )}
                  </Td>
                  <Td mono className="align-top">
                    {formatLocalTimestamp(rule.updatedAt, { seconds: true })}
                  </Td>
                  <Td className="text-right align-top">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => onDisable(rule)}
                      disabled={!rule.enabled || (disablePending && disablingRuleId === rule.id)}
                    >
                      Disable
                    </Button>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </Panel>
  );
}

function Meta({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-label text-paper-low">{label}</div>
      <div className="mt-1 truncate font-mono text-dense text-paper">{value}</div>
    </div>
  );
}

function DryRunStrip({
  result,
  pending,
}: {
  readonly result: DryRunUpstreamErrorPolicyRuleResponse;
  readonly pending: boolean;
}): JSX.Element {
  const hasResult = pending || result.publicCode !== null || result.message !== null;
  if (!hasResult) {
    return (
      <div className="rounded-lg bg-well p-3 text-dense text-paper-low" data-testid="upstream-error-policy-dry-run-result">
        Dry-run idle
      </div>
    );
  }
  return (
    <div
      className={
        result.match
          ? 'rounded-lg border border-ok-line bg-ok-dim/50 p-3 text-dense text-ok'
          : 'rounded-lg bg-well p-3 text-dense text-paper-low'
      }
      data-testid="upstream-error-policy-dry-run-result"
    >
      {pending
        ? 'Checking...'
        : !result.match
          ? 'No match'
          : result.action === 'passthrough'
            ? 'passthrough · tenant receives the upstream status and sanitized message'
            : `${result.publicCode} · HTTP ${result.httpStatus ?? 'upstream'} · ${result.errorType ?? 'default type'} · ${result.kind} · ${result.message}`}
    </div>
  );
}

const UNAVAILABLE_MESSAGE = 'The model service is temporarily unavailable. Please retry shortly.';
const BUSY_MESSAGE = 'The model service is busy. Please retry shortly.';

interface StatusClassDefaults {
  readonly publicCode: UpstreamErrorPolicyPublicCode;
  readonly kind: UpstreamErrorPolicyKind;
  readonly fixedMessage: string;
}

/**
 * What the seeded ladder in `0007_upstream_error_policy_full_range.sql` already
 * answers for each status class. The prefill used to hardcode the 400/422
 * answer, which was right while the review queue only recorded validation
 * errors and wrong for every other class once #1468 widened recording to the
 * whole 4xx/5xx range (#1474). Operators still edit whatever this suggests;
 * the point is that the suggestion describes the observation it came from.
 */
function statusClassDefaults(rawStatus: number): StatusClassDefaults {
  switch (rawStatus) {
    case 400:
    case 422:
      return {
        publicCode: 'REQUEST_NOT_SUPPORTED',
        kind: 'V01',
        fixedMessage: 'The selected model rejected this request shape.',
      };
    case 401:
    case 403:
      return { publicCode: 'MODEL_SERVICE_UNAVAILABLE', kind: 'A01', fixedMessage: UNAVAILABLE_MESSAGE };
    case 402:
      return { publicCode: 'MODEL_SERVICE_UNAVAILABLE', kind: 'Q01', fixedMessage: UNAVAILABLE_MESSAGE };
    case 404:
      return { publicCode: 'MODEL_SERVICE_UNAVAILABLE', kind: 'M01', fixedMessage: UNAVAILABLE_MESSAGE };
    case 408:
      return {
        publicCode: 'MODEL_TIMEOUT',
        kind: 'T01',
        fixedMessage: 'The model service timed out. Please retry shortly.',
      };
    case 429:
      return { publicCode: 'MODEL_BUSY', kind: 'R01', fixedMessage: BUSY_MESSAGE };
    case 529:
      return { publicCode: 'MODEL_BUSY', kind: 'B01', fixedMessage: BUSY_MESSAGE };
    default:
      // 5xx is the ladder's server-error branch; anything else lands on its
      // unclassified catch-all rather than on a validation answer.
      return rawStatus >= 500 && rawStatus <= 599
        ? { publicCode: 'MODEL_SERVICE_UNAVAILABLE', kind: 'I01', fixedMessage: UNAVAILABLE_MESSAGE }
        : { publicCode: 'MODEL_SERVICE_UNAVAILABLE', kind: 'U01', fixedMessage: UNAVAILABLE_MESSAGE };
  }
}

export function draftFromObservation(observation: UpstreamErrorObservation | null): RuleDraft {
  const terms = observation === null
    ? ''
    : extractTerms(observation.sanitizedMessage).join(', ');
  // The image-size case is read off the message, not the status, so it keeps
  // preempting the status-class answer the way it always has.
  const imageTooLarge = terms.includes('2000') || terms.includes('image');
  const defaults = statusClassDefaults(observation?.rawStatus ?? 400);
  return {
    name: observation === null ? '' : `Draft from ${observation.fingerprint}`,
    priority: '50',
    enabled: true,
    // A single-status range: the rule describes the observation it was drafted
    // from. Anything else produced `min > max` outside {400, 422} and the save
    // was rejected before it could be written.
    statusMin: observation === null ? '400' : String(observation.rawStatus),
    statusMax: observation === null ? '422' : String(observation.rawStatus),
    rawType: observation?.rawErrorType ?? '',
    rawCode: observation?.rawErrorCode ?? '',
    requiredTerms: terms,
    excludedTerms: '',
    publicCode: imageTooLarge ? 'REQUEST_IMAGE_TOO_LARGE' : defaults.publicCode,
    action: 'map',
    httpStatus: '',
    errorType: '',
    rawTypeMatch: 'exact',
    rawCodeMatch: 'exact',
    messageMode: 'fixed',
    fixedMessage: imageTooLarge ? 'Images must be 2000 pixels or smaller.' : defaults.fixedMessage,
    kind: imageTooLarge ? 'V02' : defaults.kind,
  };
}

function draftToInput(draft: RuleDraft) {
  return {
    name: draft.name.trim(),
    priority: parseIntOrDefault(draft.priority, 50),
    enabled: draft.enabled,
    statusMin: parseNullableInt(draft.statusMin),
    statusMax: parseNullableInt(draft.statusMax),
    rawType: draft.rawType.trim().length > 0 ? draft.rawType.trim() : null,
    rawCode: draft.rawCode.trim().length > 0 ? draft.rawCode.trim() : null,
    rawTypeMatch: draft.rawTypeMatch,
    rawCodeMatch: draft.rawCodeMatch,
    requiredTerms: splitTerms(draft.requiredTerms),
    excludedTerms: splitTerms(draft.excludedTerms),
    action: draft.action,
    publicCode: draft.publicCode,
    httpStatus: parseNullableInt(draft.httpStatus),
    errorType: draft.errorType.trim().length > 0 ? draft.errorType.trim() : null,
    messageMode: draft.messageMode,
    fixedMessage: draft.fixedMessage.trim(),
    kind: draft.kind,
  };
}

function extractTerms(message: string): string[] {
  const lower = message.toLowerCase();
  const terms = new Set<string>();
  if (lower.includes('many-image')) terms.add('many-image');
  if (lower.includes('2000')) terms.add('2000 pixels');
  if (lower.includes('input_schema')) terms.add('input_schema');
  if (lower.includes('validation')) terms.add('validation');
  if (terms.size === 0) {
    const first = lower.split(/\s+/).find((part) => part.length >= 5);
    if (first !== undefined) terms.add(first);
  }
  return [...terms];
}

function splitTerms(raw: string): string[] {
  return raw
    .split(',')
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

/**
 * The dropdown, plus the rule's own code when that is one this UI does not
 * suggest — otherwise the field would render blank and the first interaction
 * would change it.
 *
 * Exported for test: the page has no rule-edit flow today, so the only way to
 * reach a draft carrying an unsuggested code is to call this directly. It is
 * kept because the day an edit flow is added, its absence is a silent
 * data-rewrite bug rather than a visible one.
 */
export function publicCodeOptionsFor(
  current: UpstreamErrorPolicyPublicCode,
): Array<{ value: string; label: string }> {
  const options = PUBLIC_CODE_OPTIONS.map((code) => ({ value: code, label: code }));
  if (PUBLIC_CODE_OPTIONS.some((code) => code === current)) return options;
  return [...options, { value: current, label: `${current} (stored)` }];
}

export function toPublicCode(value: string): UpstreamErrorPolicyPublicCode {
  // Validated against the whole contract union, not the dropdown: a rule
  // carrying `UPSTREAM_PASSTHROUGH` (creatable through the internal API) would
  // otherwise be silently rewritten to `REQUEST_NOT_SUPPORTED` the moment an
  // operator opened it to edit an unrelated field.
  return KNOWN_PUBLIC_CODES.find((code) => code === value) ?? 'REQUEST_NOT_SUPPORTED';
}

function toKind(value: string): UpstreamErrorPolicyKind {
  return KIND_OPTIONS.find((kind) => kind === value) ?? 'V01';
}

function parseNullableInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseIntOrDefault(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Passed-through errors are the ones worth a rule: nothing rewrote them yet.
 * Mapped and procurement-fault observations are already handled, so they read
 * as settled rather than as work.
 */
function actionSeenVariant(action: UpstreamErrorActionSeen): 'ok' | 'warning' | 'neutral' {
  if (action === 'passthrough') return 'warning';
  if (action === 'mapped') return 'ok';
  return 'neutral';
}

function reviewVariant(status: UpstreamErrorReviewStatus): 'ok' | 'warning' | 'neutral' {
  if (status === 'unreviewed') return 'warning';
  if (status === 'rule_attached') return 'ok';
  return 'neutral';
}
